import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, FormField, Modal, ModalBody, ModalFooter, ModalHeader, Select } from '@vd/ui';
import { ArrowRight } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';

export interface MoveToProjectModalProps {
  open: boolean;
  onClose: () => void;
  currentProjectId: string;
  onMove: (projectId: string, columnId: string) => void;
}

export function MoveToProjectModal({ open, onClose, currentProjectId, onMove }: MoveToProjectModalProps) {
  const { currentCompanyId } = useAuth();

  const { data: projects = [] } = useQuery({
    queryKey: ['projects', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listProjects(currentCompanyId) : Promise.resolve([]),
    enabled: !!currentCompanyId && open,
  });

  // The modal handles BOTH cross-project moves and within-project column
  // changes (the latter is the common case for bulk-move on list/table
  // views, which can't drag-drop). So the current project stays in the
  // dropdown, and we default to it — the user changes it explicitly if
  // they want a cross-project move. Pre-fix, this hid the current project
  // and defaulted to "the first OTHER project," which silently bulk-moved
  // 50+ tickets into the wrong workspace on 2026-05-01.
  const available = projects;
  const [targetProjectId, setTargetProjectId] = useState<string>('');

  // Reset / prime state when the modal opens. Default to the current
  // project so a careless click doesn't punt tickets out of the
  // workspace. If for any reason the current project isn't in the list
  // (hypothetical — shouldn't happen), fall back to the first available.
  if (open && !targetProjectId) {
    if (currentProjectId && available.some((p) => p.id === currentProjectId)) {
      setTargetProjectId(currentProjectId);
    } else if (available[0]) {
      setTargetProjectId(available[0].id);
    }
  }

  const { data: columns = [] } = useQuery({
    queryKey: ['columns', targetProjectId],
    queryFn: () => api.listColumns(targetProjectId),
    enabled: open && !!targetProjectId,
  });
  const [targetColumnId, setTargetColumnId] = useState<string>('');

  if (open && columns.length > 0 && !columns.some((c) => c.id === targetColumnId)) {
    setTargetColumnId(columns[0]!.id);
  }

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setTargetProjectId('');
      setTargetColumnId('');
    }, 200);
  };

  const handleMove = () => {
    if (!targetProjectId || !targetColumnId) return;
    onMove(targetProjectId, targetColumnId);
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose} width={440}>
      <ModalHeader>Move ticket</ModalHeader>
      <ModalBody>
        {available.length === 0 ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            No projects to move to. Create one first.
          </div>
        ) : (
          <>
            <FormField label="Destination project">
              <Select value={targetProjectId} onChange={(e) => setTargetProjectId(e.target.value)}>
                {available.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </FormField>
            <div style={{ height: 12 }} />
            <FormField label="Column" help="Where the ticket lands in the destination project.">
              <Select value={targetColumnId} onChange={(e) => setTargetColumnId(e.target.value)}>
                {columns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </FormField>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={handleClose}>Cancel</Button>
        <Button
          variant="primary"
          disabled={available.length === 0 || !targetProjectId || !targetColumnId}
          onClick={handleMove}
          rightIcon={<ArrowRight size={14} />}
        >
          Move ticket
        </Button>
      </ModalFooter>
    </Modal>
  );
}
